import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/app/utils/useLang';
import gtm from '@/utils/gtm';
import EcoImg from 'eco-design/base/eco-img';
import EcoSpace from 'eco-design/base/eco-space';
import EcoSectionCommon from 'eco-design/elements/eco-section-common';
import { useRef } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { AnimatedGrid } from '../AnimatedSection';
import styles from './index.module.scss';

interface BuiltProps {
    pageType: 'max' | 'ultra';
}

const Built: React.FC<BuiltProps> = ({ pageType }: BuiltProps = { pageType: 'max' }) => {
    const { locale } = getLocal();
    const { t } = useLang('Built');
    const swiperRef = useRef<any>(null);
    const magicLocale = ['us', 'ca'].includes(locale) ? 'us' : 'eu';
    const isEU = ['eu', 'uk', 'de', 'fr', 'it', 'es'].includes(locale);

    const list0ImgMap = {
        max: {
            pc: isEU
                ? 'https://websiteoss.ecoflow.com/figma-uploads/1761717547824-e0pza3-compressed-Frame%201000009369.jpg'
                : 'https://websiteoss.ecoflow.com/figma-uploads/1758029131311-qqbmo5-compressed-1.jpg',
            h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758029292528-v0p78i-compressed-1.jpg',
        },
        ultra: {
            pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758541776550-nvaoli-compressed-1.jpg',
            h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758541839448-5ghr1g-compressed-1.jpg',
        },
    };

    const list1ImgMap = {
        max: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758029187055-hibdgv-compressed-2.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758029304398-y3onnf-compressed-2.jpg',
            },
            eu: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761544863626-mo6tpq-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761544994496-waq8y7-compressed-20250911.jpg',
            },
        },
        ultra: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758541785432-p1xh92-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758541878764-1w2mhc-compressed-20250911.jpg',
            },
            eu: {
                pc: '',
                h5: '',
            },
        },
    };

    const list2ImgMap = {
        max: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758029199799-05rcjv-compressed-3.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758029313060-ol7yeq-compressed-3.jpg',
            },
            eu: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761544875815-nm5mds-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761545002956-w70p22-compressed-20250912.jpg',
            },
        },
        ultra: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758541796137-1eylc5-compressed-3.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758541887729-cgbw19-compressed-3.jpg',
            },
            eu: {
                pc: '',
                h5: '',
            },
        },
    };

    const list3ImgMap: Record<'max' | 'ultra', Partial<Record<'us' | 'ca' | 'eu' | 'uk' | 'de' | 'fr' | 'it' | 'es', { pc: string; h5: string }>>> = {
        max: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758029213044-5dgo53-compressed-4.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758029321917-teiojt-compressed-4.jpg',
            },
            eu: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761289360231-4ewikq-compressed-Frame%201000009376.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761289386604-hfnamn-compressed-%E8%83%BD%E7%99%BB%E5%8D%8A%E5%B3%B6%E5%AF%84%E8%B4%88.jpg',
            },
        },
        ultra: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758541808017-ctr113-compressed-4.jpg',
                h5: 'htecoflow.tps://websiteoss.com/figma-uploads/1758541897871-arqzdy-compressed-4.jpg',
            },
            ca: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758541808017-ctr113-compressed-4.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758541897871-arqzdy-compressed-4.jpg',
            },
        },
    };

    const list1ImgResMap = list1ImgMap[pageType as keyof typeof list1ImgMap]?.[magicLocale];
    const list2ImgResMap = list2ImgMap[pageType as keyof typeof list2ImgMap]?.[magicLocale];
    const list3ImgResMap = list3ImgMap[pageType as keyof typeof list3ImgMap]?.[magicLocale];

    const list4ImgMap = {
        max: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758866161207-fwd2u4-compressed-5.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758790056349-br8yyq-compressed-5.jpg',
            },
            ca: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758866161207-fwd2u4-compressed-5.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758790056349-br8yyq-compressed-5.jpg',
            },
            eu: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761284904700-2mcank-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761284938930-etzvil-compressed-%E8%83%BD%E7%99%BB%E5%8D%8A%E5%B3%B6%E5%AF%84%E8%B4%88.jpg',
            },
            uk: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761284904700-2mcank-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761284938930-etzvil-compressed-%E8%83%BD%E7%99%BB%E5%8D%8A%E5%B3%B6%E5%AF%84%E8%B4%88.jpg',
            },
            de: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761718365790-cqlqmd-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761718425885-tcl9dx-compressed-%E8%83%BD%E7%99%BB%E5%8D%8A%E5%B3%B6%E5%AF%84%E8%B4%88.jpg',
            },
            fr: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761120191012-tqzd35-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761120171572-ejj4xd-compressed-%E8%83%BD%E7%99%BB%E5%8D%8A%E5%B3%B6%E5%AF%84%E8%B4%88.jpg',
            },
            it: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761120206163-ldta04-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761120229951-v9y5bj-compressed-%E8%83%BD%E7%99%BB%E5%8D%8A%E5%B3%B6%E5%AF%84%E8%B4%88.jpg',
            },
            es: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1761120249672-e6h0da-compressed-image.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1761120265623-io9toy-compressed-%E8%83%BD%E7%99%BB%E5%8D%8A%E5%B3%B6%E5%AF%84%E8%B4%88.jpg',
            },
        },
        ultra: {
            us: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758722743415-gclws8-compressed-5.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758722665154-jh9l8m-compressed-5.jpg',
            },
            ca: {
                pc: 'https://websiteoss.ecoflow.com/figma-uploads/1758722743415-gclws8-compressed-5.jpg',
                h5: 'https://websiteoss.ecoflow.com/figma-uploads/1758722665154-jh9l8m-compressed-5.jpg',
            },
        },
    };

    // @ts-ignore
    const list4ImgResMap = list4ImgMap[pageType as keyof typeof list4ImgMap][locale];

    const list = [
        {
            title: t.rich('list1_title', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            desc: t.rich('list1_desc', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            pc: list0ImgMap[pageType as keyof typeof list0ImgMap].pc,
            h5: list0ImgMap[pageType as keyof typeof list0ImgMap].h5,
        },
        {
            title: t.rich('list2_title', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            desc: t.rich('list2_desc', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            pc: list1ImgResMap.pc,
            h5: list1ImgResMap.h5,
        },
        {
            title: t.rich('list3_title', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            desc: t.rich('list3_desc', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            pc: list2ImgResMap.pc,
            h5: list2ImgResMap.h5,
        },
        {
            title: t.rich('list4_title', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            desc: t.rich('list4_desc', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            pc: list3ImgResMap?.pc,
            h5: list3ImgResMap?.h5,
        },
        {
            title: t.rich('list5_title', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            desc: t.rich('list5_desc', {
                sup: (text: string) => <sup>{text}</sup>,
            }),
            pc: list4ImgResMap.pc,
            h5: list4ImgResMap.h5,
        },
    ];

    const handleClick = (name: string) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: t('title'),
            button_name: name,
        });
    };
    return (
        <div className={`${styles.Built} GtmModuleData-section`} id="Built" data-section-id="d3m-built">
            <EcoSpace hPc="160px" hPad="100px" hH5="100px" />

            <AnimatedGrid variant="fadeInUp" options={{ delay: 200, stagger: 0.2 }} className="d3m-title-common">
                <EcoSectionCommon
                    className="d3m-title-common d3m-layout"
                    title={t.rich('title', {
                        sup: (text: string) => <sup>{text}</sup>,
                    })}
                    desc={t.rich('desc', {
                        sup: (text: string) => <sup>{text}</sup>,
                    })}
                />
                <div className="swiper-built d3m-layout">
                    <Swiper
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        slidesPerView={'auto'}
                        spaceBetween={24}
                        modules={[Navigation]}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev',
                        }}
                        initialSlide={0}
                    >
                        {list.map((item, index) => (
                            <SwiperSlide key={index}>
                                <div className="list-item">
                                    <div className="img-wrapper">
                                        <EcoImg pc={item.pc} pad={item.pc} h5={item.h5} isOpenLazy={true} />
                                        {index === 3 && <div className="icon-text">{t('list4_icon')}</div>}
                                    </div>

                                    <div className="text-wrapper">
                                        <div className="title">{item.title}</div>
                                        <div className="desc">{item.desc}</div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}

                        <div className="swiper-button-prev" onClick={() => handleClick('Left switch')} />
                        <div className="swiper-button-next" onClick={() => handleClick('Right switch')} />
                    </Swiper>
                </div>
            </AnimatedGrid>

            {pageType === 'max' && <EcoSpace hPc="160px" hPad="100px" hH5="100px" />}
        </div>
    );
};

export default Built;
